import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { Loader2, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogMain,
  DialogTitle,
  Button,
  Label,
  Input,
  Textarea,
  MultiSelect,
  type MultiSelectOption,
} from '@ncoder/ui';
import { useDebounce } from '@/utils/useDebounce';
import { useSearchObjects } from '@/hooks/metadata_v1';
import { WeaveIcon } from '@/components/atoms/WeaveIcon';

export interface CreateMetadataFormData {
  name: string;
  description: string;
  relatedObjects: string[];
}

interface CreateMetadataModalProps {
  open: boolean;
  onClose: () => void;
  metadataType: string | null;
  onSubmit?: (data: CreateMetadataFormData) => void | Promise<void>;
  isGenerating?: boolean;
}

const defaultValues: Omit<CreateMetadataFormData, 'relatedObjects'> = {
  name: '',
  description: '',
};

const STANDARD_OBJECTS = [
  'Account', 'Contact', 'Opportunity', 'Lead', 'Case', 'Task', 'Event',
  'Campaign', 'User', 'Order', 'Product2', 'Contract', 'Quote', 'Asset',
];

function CreateMetadataFormFields({ isGenerating }: { isGenerating: boolean }) {
  const { register } = useFormContext<CreateMetadataFormData>();

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="create-name">Name</Label>
        <Input
          id="create-name"
          placeholder="e.g. AccountHandler"
          disabled={isGenerating}
          autoFocus
          {...register('name', { required: true })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="create-description">Description</Label>
        <Textarea
          id="create-description"
          placeholder="Describe what this component should do..."
          disabled={isGenerating}
          rows={4}
          {...register('description', { required: true })}
        />
      </div>
    </div>
  );
}

export function CreateMetadataModal({
  open,
  onClose,
  metadataType,
  onSubmit,
  isGenerating = false,
}: CreateMetadataModalProps) {
  const formMethods = useForm<CreateMetadataFormData>({
    defaultValues: { ...defaultValues, relatedObjects: [] },
    mode: 'onChange',
  });

  const {
    formState: { isValid },
    handleSubmit,
    reset,
  } = formMethods;

  const [selectedObjects, setSelectedObjects] = useState<string[]>([]);
  const [objectSearch, setObjectSearch] = useState('');

  const debouncedSearch = useDebounce(objectSearch, 300);
  const { data: searchData } = useSearchObjects(debouncedSearch);

  const searchOptions = useMemo((): MultiSelectOption[] => {
    const apiMatches = (searchData ?? []).map((item) => item.fullName);
    const standardMatches = STANDARD_OBJECTS.filter((o) =>
      debouncedSearch.length < 1
        ? true
        : o.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
    return [...new Set([...standardMatches, ...apiMatches])]
      .sort()
      .map((name) => ({ label: name, value: name }));
  }, [searchData, debouncedSearch]);

  useEffect(() => {
    if (open) {
      reset({ ...defaultValues, relatedObjects: [] });
      setSelectedObjects([]);
      setObjectSearch('');
    }
  }, [open, reset]);

  const handleSearchChange = useCallback((value: string) => {
    setObjectSearch(value);
  }, []);

  const resetAndClose = () => {
    reset({ ...defaultValues, relatedObjects: [] });
    setSelectedObjects([]);
    setObjectSearch('');
    onClose();
  };

  const handleCreate = onSubmit
    ? handleSubmit(async (data) => {
        await onSubmit({ ...data, relatedObjects: selectedObjects });
      })
    : () => {};

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && resetAndClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Create New {metadataType ?? ''}</DialogTitle>
          <DialogDescription>
            Describe what you want to create and AI will generate the code for you.
          </DialogDescription>
        </DialogHeader>

        <FormProvider {...formMethods}>
          <DialogMain>
            <div className="space-y-4">
              <CreateMetadataFormFields isGenerating={isGenerating} />

              <div className="space-y-2">
                <Label>Related Objects</Label>
                <MultiSelect
                  options={searchOptions}
                  onValueChange={setSelectedObjects}
                  onSearchChange={handleSearchChange}
                  defaultValue={[]}
                  placeholder="Search and select objects..."
                  searchable
                  hideSelectAll
                  disabled={isGenerating}
                  emptyIndicator="No objects found."
                  modalPopover
                />
                <p className="text-xs text-muted-foreground">
                  Select objects this code will interact with so AI uses real field names.
                </p>
              </div>
            </div>
          </DialogMain>
        </FormProvider>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            size="sm"
            onClick={resetAndClose}
            disabled={isGenerating}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleCreate}
            disabled={!isValid || isGenerating}
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Generating...
              </>
            ) : (
              <>
                <WeaveIcon className="h-4 w-4 mr-2" />
                Weave in
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
